import React from 'react'
import { RealEstateCategory, SegmentationType, TransactionType } from '../../../../../../@types/types'
import { Flow, FlowName, FlowScreenElement } from '../../../../../../@types/flow-types'
import { PROPERTY_OWNER_SHORT_TERM_ACCOMMODATION_RENT_FLOW_PATH } from '../../../../../../services/routes/routes'
import { propertyOwnerShortTermAccommodationRentSchema } from './propertyOwnerShortTermAccommodationRentSchema'
import {
  SCREEN_ADDRESS,
  SCREEN_BASIC_INFO,
  SCREEN_CONSTRUCTION,
  SCREEN_CONTACT_INFO,
  SCREEN_DESCRIPTION,
  SCREEN_MULTIMEDIA,
  SCREEN_OTHER_FACILITIES,
  SCREENS,
} from '../../../../../../services/domain/wizardScreens'
import ContactInfo from '../../../../../../components/pages/insertionPage/screens/contactInfo/ContactInfo'
import Address from '../../../../../../components/pages/insertionPage/screens/address/Address'
import BasicInfo from '../../../../../../components/pages/insertionPage/screens/basicInfo/BasicInfo'
import Description from '../../../../../../components/pages/insertionPage/screens/description/Description'
import OtherFacilities from '../../../../../../components/pages/insertionPage/screens/otherFacilities/OtherFacilities'
import {
  floorAndTotalFloor,
  isFurnished,
  maxNumberOfPersons,
  maxRentalTime,
  minRentalTime,
  parkingSpace,
  petsAllowed,
} from '../../../../../../components/pages/insertionPage/screens/otherFacilities/wrapper/otherFacilitiesFieldsWrapper'
import {
  additionalCostsField,
  depositField,
  livingSpaceField,
  priceField,
  pricePeriodTypeField,
  roomField,
  startRentalDateField,
} from '../../../../../../components/pages/insertionPage/screens/basicInfo/wrappers/basicInfoFieldsWrapper'
import { PriceFieldType } from '../../../../../../components/pages/insertionPage/screens/basicInfo/fields/priceField/PriceField'
import { CalendaredRentPriceTypeOption } from '../../../../../../@types/dropdown-types'
import {
  buildingTypeField,
  BuildingTypeTranslationKey,
} from '../../../../../../components/pages/insertionPage/screens/commonFields/buildingType/BuildingType'
import { priceToApiShortTermRent } from '../../../../../../api/realEstate/converters/toApi/estate/price/priceToApi'
import { getDefaultClasses } from '../../../../../../utils/style-utils'
import Multimedia from '../../../../../../components/pages/insertionPage/screens/multimediaNew/Multimedia'

import { MultimediaUploaderType } from '../../../../../../@types/multimedia-types'
import Construction from '../../../../../../components/pages/insertionPage/screens/construction/Construction'
import { shortTermAccommodationRentBuildingTypeOptions, shortTermAccommodationRentEstateFeatures } from '../common'

const propertyOwnerShortTermAccommodationRentFlowScreens: FlowScreenElement[] = [
  {
    screen: SCREENS[SCREEN_ADDRESS],
    element: (props) => <Address {...props} />,
  },
  {
    screen: SCREENS[SCREEN_BASIC_INFO],
    element: (props) => (
      <BasicInfo
        {...props}
        screenTooltipKey='tooltip.screens.basicInfo.apartmentHouseOrSTARent'
        properties={[
          priceField(PriceFieldType.FLAT_RATE_RENT),
          pricePeriodTypeField(),
          roomField(getDefaultClasses('leftOnAll')),
          startRentalDateField(getDefaultClasses('rightOnAll')),
          livingSpaceField(),
          buildingTypeField(
            'shortTermLiving',
            shortTermAccommodationRentBuildingTypeOptions,
            getDefaultClasses('right'),
            BuildingTypeTranslationKey.SHORT_TERM_ACCOMMODATION
          ),
          additionalCostsField(),
          depositField(),
        ]}
      />
    ),
  },
  {
    screen: SCREENS[SCREEN_OTHER_FACILITIES],
    element: (props) => (
      <OtherFacilities
        {...props}
        screenTooltipKey='tooltip.screens.otherFacilities.allRent'
        properties={[
          shortTermAccommodationRentEstateFeatures(),
          isFurnished(),
          petsAllowed(),
          maxNumberOfPersons(getDefaultClasses('left')),
          minRentalTime(getDefaultClasses('right')),
          maxRentalTime(getDefaultClasses('left')),
          floorAndTotalFloor('right'),
          parkingSpace({ showParkingSpaceType: true, hasGarageLabel: true }),
        ]}
      />
    ),
  },
  {
    screen: SCREENS[SCREEN_DESCRIPTION],
    element: (props) => <Description {...props} hasFurnishingNote={true} hasLocationNote={true} hasOtherNote={true} />,
  },
  {
    screen: SCREENS[SCREEN_MULTIMEDIA],
    element: (props) => (
      <Multimedia
        {...props}
        doCloseMultimediaStatusMessage={props.doCloseMultimediaStatusMessage!}
        allowedTypesToDisplay={[
          MultimediaUploaderType.PICTURE,
          MultimediaUploaderType.FLOOR_PLAN,
          MultimediaUploaderType.DOCUMENT,
          MultimediaUploaderType.VIDEO,
        ]}
      />
    ),
  },
  {
    screen: SCREENS[SCREEN_CONSTRUCTION],
    element: (props) => <Construction {...props} hasLastModernizationField={false} />,
  },
  {
    screen: SCREENS[SCREEN_CONTACT_INFO],
    element: (props) => <ContactInfo {...props} />,
  },
]

export const propertyOwnerShortTermAccommodationRentFlow: Flow = {
  flowInformation: {
    owner: 'wizard.navigation.header.owner',
    estateType: 'wizard.navigation.header.short_term_accommodation',
    offerType: 'wizard.navigation.header.rents',
    path: PROPERTY_OWNER_SHORT_TERM_ACCOMMODATION_RENT_FLOW_PATH,
    name: FlowName.PROPERTY_OWNER_SHORT_TERM_ACCOMMODATION_RENT,
    validationSchema: propertyOwnerShortTermAccommodationRentSchema,
    options: {
      hasTextSuggestions: true,
    },
    metaData: {
      category: RealEstateCategory.SHORT_TERM_ACCOMMODATION,
      segmentation: SegmentationType.PROPERTY_OWNER,
      transaction: TransactionType.RENT,
    },
    priceToApiMapper: priceToApiShortTermRent,
  },
  defaultStateValues: {
    // TODO add default
    hideStreetAndStreetNumber: false,
    pricePeriodType: { value: CalendaredRentPriceTypeOption.DAY, label: 'Tag' },
  },
  flowScreens: propertyOwnerShortTermAccommodationRentFlowScreens,
}
